import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import backendUrl from '../config';

interface User {
  id: string;
  username: string;
  email: string;
}

interface Summary {
  id: string;
  title: string;
  status: string;
}

export interface Process {
  id: string;
  processid: string;
  name?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  user_id: string;
  created_at: string;
  original_filename?: string;
  file_size?: number;
  error_message?: string;
  completed_at?: string;
  user?: User;

  summaries?: Summary[];
}

export interface ProcessStats {
  total_processes: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

interface ProcessesState {
  processes: Process[];
  currentProcess: Process | null;
  stats: ProcessStats | null;
  loading: boolean;
  error: string | null;
  statusFilter: string;
}

const initialState: ProcessesState = {
  processes: [],
  currentProcess: null,
  stats: null,
  loading: false,
  error: null,
  statusFilter: 'all',
};

// Async thunks
export const fetchProcesses = createAsyncThunk(
  'processes/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/processes`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch processes');
    }
  }
);

export const fetchProcess = createAsyncThunk(
  'processes/fetchOne',
  async (id: string, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/processes/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch process');
    }
  }
);

export const deleteProcess = createAsyncThunk(
  'processes/delete',
  async (id: string, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      await axios.delete(`${backendUrl}/api/processes/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete process');
    }
  }
);

export const startIngestion = createAsyncThunk(
  'processes/startIngestion',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.post(
        `${backendUrl}/api/ingest`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to start ingestion');
    }
  }
);

export const stopIngestion = createAsyncThunk(
  'processes/stopIngestion',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.post(
        `${backendUrl}/api/stop-ingestion`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to stop ingestion');
    }
  }
);

export const testConnections = createAsyncThunk(
  'processes/testConnections',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.post(
        `${backendUrl}/api/test-connections`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to test connections');
    }
  }
);

// New thunk for fetching all processes with summaries
export const fetchAllProcesses = createAsyncThunk(
  'processes/fetchAllWithSummaries',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/processes`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch processes');
    }
  }
);

// New thunk for fetching process statistics
export const fetchProcessStats = createAsyncThunk(
  'processes/fetchStats',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/processes/stats/summary`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch process statistics');
    }
  }
);

const processesSlice = createSlice({
  name: 'processes',
  initialState,
  reducers: {
    setCurrentProcess: (state, action: PayloadAction<Process | null>) => {
      state.currentProcess = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.statusFilter = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateProcessStatus: (state, action: PayloadAction<{ id: string; status: string; error_message?: string; completed_at?: string }>) => {
      const process = state.processes.find(p => p.id === action.payload.id);
      if (process) {
        process.status = action.payload.status as any;
        if (action.payload.error_message) {
          process.error_message = action.payload.error_message;
        }
        if (action.payload.completed_at) {
          process.completed_at = action.payload.completed_at;
        }
      }
    },
    addProcess: (state, action: PayloadAction<Process>) => {
      state.processes.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Processes
      .addCase(fetchProcesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProcesses.fulfilled, (state, action) => {
        state.loading = false;
        state.processes = action.payload;
      })
      .addCase(fetchProcesses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Single Process
      .addCase(fetchProcess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProcess.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProcess = action.payload;
      })
      .addCase(fetchProcess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Process
      .addCase(deleteProcess.fulfilled, (state, action) => {
        state.processes = state.processes.filter(p => p.id !== action.payload);
      })
      .addCase(deleteProcess.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Start Ingestion
      .addCase(startIngestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startIngestion.fulfilled, (state, action) => {
        state.loading = false;
        // Add new process if returned
        if (action.payload.process) {
          state.processes.unshift(action.payload.process);
        }
      })
      .addCase(startIngestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Stop Ingestion
      .addCase(stopIngestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(stopIngestion.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(stopIngestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Test Connections
      .addCase(testConnections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testConnections.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(testConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All Processes with Summaries
      .addCase(fetchAllProcesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProcesses.fulfilled, (state, action) => {
        state.loading = false;
        state.processes = action.payload;
      })
      .addCase(fetchAllProcesses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Process Statistics
      .addCase(fetchProcessStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProcessStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchProcessStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentProcess,
  setStatusFilter,
  clearError,
  updateProcessStatus,
  addProcess,
} = processesSlice.actions;

export default processesSlice.reducer;
